import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent, BubbleMenu, FloatingMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import { ResizableImage } from './ResizableImageExtension';
import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon,
  List, 
  ListOrdered, 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  AlignJustify,
  Link as LinkIcon,
  Image as ImageIcon,
  Highlighter,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Code,
  Palette,
  Undo,
  Redo,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import ImageUploader from '@/components/shared/ImageUploader';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const preventFormSubmit = (e: React.MouseEvent | React.KeyboardEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Write something awesome...',
}) => {
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlignment, setImageAlignment] = useState<'left' | 'center' | 'right'>('center');
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  // Add styles for editor placeholder and resizable images
  const editorStyles = `
    .ProseMirror p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      float: left;
      color: #adb5bd;
      pointer-events: none;
      height: 0;
    }
    .ProseMirror .is-empty::before {
      content: attr(data-placeholder);
      float: left;
      color: #adb5bd;
      pointer-events: none;
      height: 0;
    }
    .ProseMirror {
      background-color: transparent !important;
      min-height: 500px;
      height: 100%;
      width: 100%;
      padding: 16px;
    }
    .ProseMirror p {
      margin: 0.5em 0;
      background-color: transparent !important;
      min-height: 1.5em;
    }
    .editor-container {
      position: relative;
      min-height: 500px;
      cursor: text;
      display: flex;
      flex-direction: column;
      flex: 1;
    }
    .editor-container .ProseMirror-focused {
      outline: none;
    }
    
    /* Bullet and Ordered List Styles */
    .ProseMirror ul {
      list-style-type: disc;
      padding-left: 1.5em;
      margin: 0.5em 0;
    }
    .ProseMirror ol {
      list-style-type: decimal;
      padding-left: 1.5em;
      margin: 0.5em 0;
    }
    .ProseMirror li {
      margin-bottom: 0.25em;
    }
    .ProseMirror li p {
      margin: 0;
    }
    
    /* Resizable image styles */
    .resizable-image-wrapper {
      position: relative;
      margin: 1em 0;
      display: block;
      width: 100%;
    }
    .resizable-image-wrapper[data-alignment="left"] {
      margin-right: auto;
      margin-left: 0;
    }
    .resizable-image-wrapper[data-alignment="center"] {
      margin-left: auto;
      margin-right: auto;
      text-align: center;
    }
    .resizable-image-wrapper[data-alignment="right"] {
      margin-left: auto;
      margin-right: 0;
      text-align: right;
    }
    .resizable-image-wrapper.ProseMirror-selectednode {
      outline: 2px solid #0ea5e9;
    }
  `;

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline underline-offset-2',
        },
      }),
      ResizableImage.configure({
        inline: false,
        HTMLAttributes: {
          class: 'rounded-lg max-w-full',
        },
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty',
        emptyNodeClass: 'is-node-empty',
        showOnlyWhenEditable: true,
      }),
      Highlight.configure({
        HTMLAttributes: {
          class: 'bg-yellow-200 rounded px-1',
        },
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph', 'resizableImage'],
      }),
      TextStyle,
      Color,
    ],
    content: value,
    onUpdate: ({ editor }: { editor: any }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'outline-none w-full h-full min-h-[500px]',
        spellcheck: 'true',
      },
    },
    autofocus: true,
  });

  // Update editor content when value changes externally
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [editor, value]);

  if (!editor) {
    return null;
  }

  // Helper function to apply the link
  const applyLink = () => {
    // If empty, remove the link
    if (!linkUrl.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      // Add or update the link
      editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
    }
    
    // Close dialog and reset state
    setLinkDialogOpen(false);
  };

  // Helper function to add an image by URL
  const addImageByUrl = () => {
    if (imageUrl && editor) {
      editor.chain().focus().setResizableImage({ 
        src: imageUrl,
        alignment: imageAlignment 
      }).run();
      setImageUrl('');
      setImageDialogOpen(false);
    }
  };

  // Helper function to add an image after upload
  const handleImageUploaded = (url: string) => {
    if (editor) {
      editor.chain().focus().setResizableImage({ 
        src: url,
        alignment: imageAlignment 
      }).run();
      setImageDialogOpen(false);
    }
  };

  // Open image dialog
  const openImageDialog = () => {
    setImageUrl('');
    setImageAlignment('center');
    setImageDialogOpen(true);
  };

  // Function to focus the editor when clicking anywhere in the container
  const handleContainerClick = (event: React.MouseEvent) => {
    // Prevent default browser behavior
    event.preventDefault();
    
    // Only focus if we're clicking the container directly, not a child element
    if (event.target === event.currentTarget) {
      editor.commands.focus('end');
    }
  };

  // Function to safely open link dialog without form submission
  const linkButtonClickHandler = (e?: React.MouseEvent) => {
    // Prevent the event from bubbling up to any parent forms
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Get the current URL if we're updating an existing link
    const previousUrl = editor.getAttributes('link').href;
    setLinkUrl(previousUrl || '');
    
    // Use setTimeout to defer opening the dialog until after the click event has fully resolved
    setTimeout(() => {
      setLinkDialogOpen(true);
    }, 10);
  };

  const MenuButton = ({ 
    icon, 
    onClick, 
    isActive = false, 
    tooltip 
  }: { 
    icon: React.ReactNode, 
    onClick: (e?: React.MouseEvent) => void, 
    isActive?: boolean,
    tooltip: string 
  }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button 
            variant={isActive ? "default" : "ghost"} 
            size="icon" 
            onClick={onClick}
            className={`h-8 w-8 rounded-md ${isActive ? '' : 'text-foreground hover:text-foreground'}`}
          >
            {icon}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <div className="rounded-md border border-input">
      <style>{editorStyles}</style>
      {/* Main toolbar */}
      <div className="flex flex-wrap gap-1 p-1 border-b">
        <MenuButton 
          icon={<Bold className="h-4 w-4" />} 
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          tooltip="Bold"
        />
        <MenuButton 
          icon={<Italic className="h-4 w-4" />} 
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          tooltip="Italic"
        />
        <MenuButton 
          icon={<UnderlineIcon className="h-4 w-4" />} 
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          tooltip="Underline"
        />
        <MenuButton 
          icon={<Highlighter className="h-4 w-4" />} 
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          isActive={editor.isActive('highlight')}
          tooltip="Highlight"
        />
        
        <div className="w-px h-8 bg-border mx-1"></div>
        
        <MenuButton 
          icon={<Heading1 className="h-4 w-4" />} 
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          tooltip="Heading 1"
        />
        <MenuButton 
          icon={<Heading2 className="h-4 w-4" />} 
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          tooltip="Heading 2"
        />
        <MenuButton 
          icon={<Heading3 className="h-4 w-4" />} 
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          tooltip="Heading 3"
        />
        
        <div className="w-px h-8 bg-border mx-1"></div>
        
        <MenuButton 
          icon={<List className="h-4 w-4" />} 
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          tooltip="Bullet List"
        />
        <MenuButton 
          icon={<ListOrdered className="h-4 w-4" />} 
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          tooltip="Ordered List"
        />
        <MenuButton 
          icon={<Quote className="h-4 w-4" />} 
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          tooltip="Quote"
        />
        <MenuButton 
          icon={<Code className="h-4 w-4" />} 
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          isActive={editor.isActive('codeBlock')}
          tooltip="Code Block"
        />
        
        <div className="w-px h-8 bg-border mx-1"></div>
        
        <MenuButton 
          icon={<AlignLeft className="h-4 w-4" />} 
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive({ textAlign: 'left' })}
          tooltip="Align Left"
        />
        <MenuButton 
          icon={<AlignCenter className="h-4 w-4" />} 
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive({ textAlign: 'center' })}
          tooltip="Align Center"
        />
        <MenuButton 
          icon={<AlignRight className="h-4 w-4" />} 
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive({ textAlign: 'right' })}
          tooltip="Align Right"
        />
        <MenuButton 
          icon={<AlignJustify className="h-4 w-4" />} 
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          isActive={editor.isActive({ textAlign: 'justify' })}
          tooltip="Align Justify"
        />
        
        <div className="w-px h-8 bg-border mx-1"></div>
        
        <MenuButton 
          icon={<LinkIcon className="h-4 w-4" />} 
          onClick={linkButtonClickHandler}
          isActive={editor.isActive('link')}
          tooltip="Add Link"
        />
        <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-md text-foreground hover:text-foreground"
              onClick={openImageDialog}
            >
              <ImageIcon className="h-4 w-4" />
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Insert Image</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <p className="text-sm font-medium">Upload Image</p>
                <ImageUploader 
                  onImageUploaded={handleImageUploaded}
                  buttonText="Select & Upload Image"
                  buttonVariant="default"
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium">Or use an image URL</p>
                <div className="flex items-center gap-2">
                  <Input
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                  />
                  <Button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      addImageByUrl();
                    }} 
                    disabled={!imageUrl}
                    type="button"
                  >
                    Insert
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium">Image Alignment</p>
                <div className="flex items-center gap-2">
                  <Button 
                    variant={imageAlignment === 'left' ? 'default' : 'outline'}
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setImageAlignment('left');
                    }}
                    className="flex-1"
                    type="button"
                  >
                    <AlignLeft className="h-4 w-4 mr-2" />
                    Left
                  </Button>
                  <Button 
                    variant={imageAlignment === 'center' ? 'default' : 'outline'}
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setImageAlignment('center');
                    }}
                    className="flex-1"
                    type="button"
                  >
                    <AlignCenter className="h-4 w-4 mr-2" />
                    Center
                  </Button>
                  <Button 
                    variant={imageAlignment === 'right' ? 'default' : 'outline'}
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setImageAlignment('right');
                    }}
                    className="flex-1"
                    type="button"
                  >
                    <AlignRight className="h-4 w-4 mr-2" />
                    Right
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        
        <div className="w-px h-8 bg-border mx-1"></div>
        
        <MenuButton 
          icon={<Undo className="h-4 w-4" />} 
          onClick={() => editor.chain().focus().undo().run()}
          tooltip="Undo"
        />
        <MenuButton 
          icon={<Redo className="h-4 w-4" />} 
          onClick={() => editor.chain().focus().redo().run()}
          tooltip="Redo"
        />
      </div>
      
      {/* Link Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Insert Link</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="space-y-2">
              <p className="text-sm font-medium">URL</p>
              <div className="flex items-center gap-2">
                <Input
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyLink();
                    }
                  }}
                  autoFocus
                />
                <Button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    applyLink();
                  }} 
                  type="button"
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Editor content */}
      <div 
        className="editor-container relative bg-transparent border-t border-input"
        onClick={handleContainerClick}
      >
        <EditorContent 
          editor={editor} 
          className="prose prose-sm sm:prose-base lg:prose-lg max-w-none focus:outline-none overflow-y-auto resize-vertical bg-transparent" 
        />
      </div>
      
      {/* Bubble menu for quick formatting of selected text */}
      {editor && (
        <BubbleMenu 
          editor={editor} 
          tippyOptions={{ duration: 100 }}
          className="bg-background rounded-md shadow-md border border-border overflow-hidden flex items-center"
        >
          <Button 
            variant={editor.isActive('bold') ? "default" : "ghost"} 
            size="sm" 
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`px-2 py-1 h-auto ${editor.isActive('bold') ? '' : 'text-foreground hover:text-foreground'}`}
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button 
            variant={editor.isActive('italic') ? "default" : "ghost"} 
            size="sm" 
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`px-2 py-1 h-auto ${editor.isActive('italic') ? '' : 'text-foreground hover:text-foreground'}`}
          >
            <Italic className="h-4 w-4" />
          </Button>
          <Button 
            variant={editor.isActive('highlight') ? "default" : "ghost"} 
            size="sm" 
            onClick={() => editor.chain().focus().toggleHighlight().run()}
            className={`px-2 py-1 h-auto ${editor.isActive('highlight') ? '' : 'text-foreground hover:text-foreground'}`}
          >
            <Highlighter className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={linkButtonClickHandler}
            className="px-2 py-1 h-auto text-foreground hover:text-foreground"
            type="button"
          >
            <LinkIcon className="h-4 w-4" />
          </Button>
        </BubbleMenu>
      )}
    </div>
  );
};

export default RichTextEditor; 